/* eslint-disable no-restricted-syntax -- this replica's surround is a
   GRADIENT DERIVED FROM THE CREATIVE, not a Mora surface. Every other card
   in this directory sources its chrome from `PLATFORM_SKINS`, which flips
   light/dark with Mora's theme; a sponsored story has no such skin because
   the platform draws no card at all — the whole 338×718 canvas behind the
   creative comes from the app itself (typically a blur/gradient extension of
   the media), and Figma frames `101:1809`, `101:1783`, `101:1834` all render
   the IDENTICAL gradient regardless of which of the three variants they are.
   So the surround here is built from the caller's own `brandColor`, and the
   chrome drawn over it (progress bar, header text, icons, CTA pill) is
   white/near-black in EVERY theme, matching `InstagramStory.tsx`'s and
   `InstagramReel.tsx`'s precedent for full-bleed media chrome that must not
   invert with `prefers-color-scheme`. No hex literal remains outside this
   scope; `brandColor` itself is caller data, not a hardcoded value. */
import {
  ChatCircleIcon,
  DotsThreeVerticalIcon,
  HeartIcon,
  MusicNoteIcon,
  PaperPlaneTiltIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PlatformCloseIcon, PlatformMoreHorizIcon } from './PlatformIcons';
import { PreviewAvatar } from './PreviewAvatar';
import { isDarkColor, resolveAspectRatio } from './types';

import { cn } from '@/lib/utils';

/**
 * SPONSORED STORY — a full-bleed vertical placement (Instagram/Facebook
 * Stories), distinct from the organic `InstagramStory.tsx` shell it borrows
 * conventions from.
 *
 * ── PROVENANCE ────────────────────────────────────────────────────────────
 * Ported from Figma `Dl0gE7lH27N7x7OJb0DFBw`, frames `101:1809`, `101:1783`,
 * `101:1834`, read 2026-08-21. Frame `101:1809` is 338×718 and every
 * coordinate below is relative to it:
 *   - Progress track: 326×4 rounded, x=4 y=8 (see the zero-progress note
 *     below for why the frame's 37%-filled state is not reproduced).
 *   - Header at y=28: 36×36 avatar at x=16; brand name + "Sponsored" in a
 *     two-line block at x=48, the second line 17px below the first; `⋯` at
 *     x=265 y=34; `✕` at x=301 y=34 (both 24×24).
 *   - Creative: full-bleed rounded rect 338×235 at y=96 — a ~1.44:1 landscape
 *     band, NOT the full remaining height. The rest of the canvas is the
 *     gradient surround, not more media.
 *   - Body text: 306 wide at x=16 y=351, white, semibold, ~5 lines.
 *   - Swipe-up chevron: 16×16 centred at x=161 y=635.
 *   - CTA pill: 84×32 centred at x=127 y=662, light fill / dark text.
 *   - Surround: a single vertical gradient behind everything, sampled at
 *     roughly `#bf7a37` (top) → `#86511f` (bottom), identical across all
 *     three frames — see the file-level eslint-disable above for why this
 *     is built from `brandColor` rather than a fixed hex or a platform skin.
 *
 * ── WHAT THE FIGMA SHOWS THAT THIS DELIBERATELY DOES NOT RENDER ───────────
 * The frame prints a brand called "Club Doggo" and sample dog-walking copy —
 * neither belongs in code or in a test (`__tests__/engagementTruth.test.tsx`
 * states the family rule: structure and skin from Figma, never the data).
 * The progress bar sits at 37% in the frame; this component renders it at
 * ZERO. A static preview of an unpublished story has had no playback, and a
 * partly-elapsed bar invents a session that never happened —
 * `InstagramReel.tsx`/`InstagramStory.tsx` both hardcode fractional progress
 * (one of them commented as tuned "to read as 'playing'"), and this file
 * deliberately does not copy that.
 *
 * ── CHEVRON GLYPH ─────────────────────────────────────────────────────────
 * `PlatformIcons.tsx` has no chevron-up (only left/right). Rather than grow
 * that file for one arrow, this draws a two-line inline SVG chevron: the
 * swipe-up affordance is a directional hint, not a platform wordmark or
 * brand glyph, so an approximation carries none of the "wrong platform
 * identity" risk the `PlatformIcons.tsx` header warns about for things like
 * the like/comment/share row. If a third caller ever needs an up-chevron,
 * that is the moment to promote it into `PlatformIcons.tsx` — not before.
 *
 * ── NOT A `PlatformPreviewKind` ───────────────────────────────────────────
 * Same reason as `MetaAdPreview`/`TwitterAdPreview`: the dispatcher maps a
 * stored `posts.platforms` value to an organic shell, and an ad unit never
 * carries one.
 */
export interface StoryAdPreviewProps {
  brandName: string;
  /** Seeds both the avatar fallback disc AND the gradient surround. */
  brandColor?: string;
  avatarUrl?: string | null;
  /** Story body copy. Renders nothing (not placeholder text) when empty. */
  bodyText?: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  isLoading?: boolean;
  emptyHint?: string;
  /**
   * The swipe-up destination label. A story ad with no CTA has nothing to
   * swipe to, so the pill (and the chevron above it) is omitted entirely
   * rather than rendered empty or with a placeholder like "Learn more."
   */
  cta?: string;
  /**
   * The creative's own aspect ratio (width / height, or a token like `'9:16'`).
   *
   * The Figma frame draws a 338×235 band because its sample creative is
   * landscape — 338/235 IS 1.44:1, so that number was never a design decision
   * about story ads, it was one image's shape. A 9:16 creative in a real story
   * ad fills far more of the canvas, so the band follows the creative and the
   * gradient surround is whatever is left. Omitted, it falls back to the
   * frame's own 1.44:1 so nothing that already renders changes.
   */
  aspectRatio?: number | string | null;
  /**
   * Layered over the media region only, above the image — the seam the ad
   * workbench's Preview mode uses to composite the `overlay_layers` the user
   * authored in Design mode onto the same creative. Same contract as
   * `MetaAdPreviewProps.overlay`. Never rendered when there is no creative to
   * sit on: painting a headline over empty air would be a claim the caller
   * did not make.
   */
  overlay?: React.ReactNode;
  /**
   * Rendered INSIDE a frame that already sizes it. Standalone the card pins
   * itself to the Figma frame's own width (338px) and centres. Same
   * contract as `TwitterAdPreview.embedded`/`MetaAdPreview.embedded`.
   */
  embedded?: boolean;
  /**
   * Renders the creative FULL-BLEED — filling the entire canvas as the
   * background layer, with every chrome element (header, text, CTA)
   * layered on top over a scrim, the way a real Story/Reels ad actually
   * looks. Ported from `InstagramReel.tsx`'s already-shipped organic
   * full-bleed vertical structure (right rail / bottom identity block /
   * scrim gradient), adapted for an ad context rather than a second
   * from-scratch build.
   *
   * The DEFAULT (non-full-bleed) layout keeps the ORIGINAL Figma
   * `Dl0gE7lH27N7x7OJb0DFBw` composition exactly as ported: a landscape
   * 338×235 media BAND under fixed-height header/text/CTA chrome, sized to
   * a 338×718 canvas. That composition only holds together at its own
   * design width with a landscape-ish creative — passing a portrait (9:16)
   * `aspectRatio` into the non-full-bleed layout starves the fixed-height
   * chrome of room and the media wrapper (the only `overflow-hidden`,
   * hence shrinkable, flex child) collapses to 0px. `fullBleed` exists
   * specifically for a placement whose real anatomy IS full-bleed vertical
   * (Stories, Reels) — passing a 9:16 `aspectRatio` here sizes the OUTER
   * canvas itself to that ratio, so the media never has to fight fixed
   * chrome for a share of a differently-shaped box.
   */
  fullBleed?: boolean;
  /**
   * Which full-bleed chrome to render — ignored unless `fullBleed`.
   * `'story'` keeps this file's own top progress+header / bottom body+CTA
   * composition, now layered over full-bleed media instead of a landscape
   * band. `'reels'` renders Reels' OWN anatomy — a right action rail and a
   * bottom-left identity block with an audio-attribution line, structurally
   * ported from `InstagramReel.tsx` (see that file's header for its own
   * 2025-2026 Reels UI research) rather than reusing Stories' chrome for a
   * placement that looks nothing like it in the real product. The action
   * icons render with no counts (decorative, `aria-hidden`) — this is an
   * unpublished draft with no real engagement to show, never invented
   * zeros. Default `'story'`.
   */
  chrome?: 'story' | 'reels';
  className?: string;
}

/** The Figma frame's own width (`101:1809` is 338px). */
const STORY_AD_MAX_WIDTH = 338;

/** Frame is 338×718 — used for the standalone aspect box. */
const STORY_AD_RATIO = 338 / 718;

/** Creative band is 338×235 at y=96 — landscape, not full-bleed. */
const STORY_AD_MEDIA_RATIO = 338 / 235;

/**
 * Default surround when no `brandColor` is supplied — a neutral warm-grey
 * gradient base rather than inventing the Figma's specific orange, which is
 * that frame's sampled creative colour, not a platform or Mora default.
 */
const STORY_AD_DEFAULT_COLOR = '#4B4B4B';

/**
 * Darkens a hex colour toward black by `amount` (0..1), for the gradient's
 * bottom stop. Sampled off the Figma frames the top-to-bottom shift is
 * roughly a 20-25% darkening, not a fixed target hex — deriving it from the
 * caller's own colour keeps every brand's surround looking like its own
 * creative rather than converging on one hardcoded hue.
 */
function darken(hex: string, amount: number): string {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return hex;
  const num = parseInt(match[1], 16);
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)));
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** The swipe-up chevron — see the file header's "CHEVRON GLYPH" note. */
function SwipeUpChevron({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true" fill="none">
      <path
        d="M3 10.5L8 5.5L13 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const RailAction: React.FC<{ icon: React.ReactNode }> = ({ icon }) => (
  <span className="flex flex-col items-center text-white" aria-hidden="true">
    {icon}
  </span>
);

/**
 * Full-bleed 'story' chrome — the same composition as the default (non
 * full-bleed) layout below, just layered OVER the media as an overlay
 * instead of stacked under fixed-height chrome. See `fullBleed`'s doc
 * comment on `StoryAdPreviewProps`.
 */
function StoryFullBleedOverlay({
  brandName,
  brandColor,
  avatarUrl,
  bodyText,
  cta,
}: {
  brandName: string;
  brandColor: string;
  avatarUrl?: string | null;
  bodyText?: string;
  cta?: string;
}): JSX.Element {
  const avatarIsDark = isDarkColor(brandColor);
  return (
    <div className="relative flex h-full flex-col px-4 pt-2">
      <div className="h-1 w-full overflow-hidden rounded-full bg-white/35" aria-hidden>
        <div className="h-full w-0 rounded-full bg-white" />
      </div>

      <header className="mt-4 flex items-center gap-3">
        <PreviewAvatar
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          className="size-9 rounded-full border border-white/40"
          textClassName={cn('text-caption font-semibold', avatarIsDark ? 'text-white' : '')}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{brandName}</p>
          <p className="text-detail text-white/75">Sponsored</p>
        </div>
        <PlatformMoreHorizIcon size={18} className="shrink-0 text-white/80" />
        <PlatformCloseIcon size={16} className="shrink-0 text-white/90" />
      </header>

      {bodyText ? (
        // line-clamp-2, matching `ReelsFullBleedOverlay` below and the
        // organic `InstagramReel`/`YouTubeShort` precedent: a real Meta
        // Stories ad does not render unbounded primary text. Fixed
        // 2026-09-03 — unclamped text grew upward past the scrim's strong
        // band into the fully-transparent middle window this file's scrim
        // used to carry (see the `background` comment below), measured at
        // ~1.6:1 over a real photo where the AA floor is 4.5:1.
        <p className="mt-auto line-clamp-2 whitespace-pre-line text-sm font-semibold leading-relaxed text-white [text-shadow:0_1px_3px_rgb(0_0_0_/_0.6)]">
          {bodyText}
        </p>
      ) : (
        <div className="mt-auto" />
      )}

      <div className="flex flex-col items-center gap-2 pb-6 pt-4">
        {cta ? (
          <>
            <SwipeUpChevron className="size-4 text-white" />
            <div className="flex h-8 min-w-[84px] items-center justify-center rounded-full bg-white px-4 text-detail font-semibold text-black">
              {cta}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Full-bleed 'reels' chrome — right action rail + bottom-left identity
 * block + audio-attribution line, structurally ported from the organic
 * `InstagramReel.tsx` (that file's own header has the UI research this
 * anatomy is based on). Adapted for an ad unit: "Sponsored" replaces
 * "Follow", and the CTA is the swipe-up pill `StoryAdPreview` already uses
 * elsewhere in this file, rather than Reels' own bottom sheet CTA (Mora has
 * no bottom-sheet chrome to port that from).
 */
function ReelsFullBleedOverlay({
  brandName,
  brandColor,
  avatarUrl,
  bodyText,
  cta,
}: {
  brandName: string;
  brandColor: string;
  avatarUrl?: string | null;
  bodyText?: string;
  cta?: string;
}): JSX.Element {
  return (
    <div className="relative flex h-full flex-col justify-end">
      <div className="absolute right-2.5 top-1/2 flex -translate-y-1/2 flex-col items-center gap-4">
        <RailAction icon={<HeartIcon className="size-6" weight="regular" />} />
        <RailAction icon={<ChatCircleIcon className="size-6" weight="regular" />} />
        <RailAction icon={<PaperPlaneTiltIcon className="size-6" weight="regular" />} />
        <DotsThreeVerticalIcon className="size-5 text-white" weight="bold" aria-hidden />
        <span className="size-5 overflow-hidden rounded-md border-2 border-white/80" aria-hidden>
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-full rounded-none"
            textClassName="text-micro font-semibold"
          />
        </span>
      </div>

      <div className="space-y-2 px-4 pb-6 pr-14 text-white">
        <div className="flex min-w-0 items-center gap-2">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-8 shrink-0 rounded-full border border-white/40"
            textClassName="text-detail font-semibold"
          />
          <p className="truncate text-caption font-semibold">{brandName}</p>
          <span className="shrink-0 text-detail text-white/75">Sponsored</span>
        </div>
        {bodyText ? (
          <p className="line-clamp-2 text-detail leading-relaxed [text-shadow:0_1px_3px_rgb(0_0_0_/_0.6)]">
            {bodyText}
          </p>
        ) : null}
        <div className="flex items-center gap-1.5 text-detail text-white/85">
          <MusicNoteIcon className="size-3 shrink-0" weight="fill" aria-hidden />
          <span className="truncate">Original audio · {brandName}</span>
        </div>
        {cta ? (
          <div className="flex h-8 w-fit min-w-[84px] items-center justify-center rounded-full bg-white px-4 text-detail font-semibold text-black">
            {cta}
          </div>
        ) : null}
      </div>

      {/* Single thin playback line at zero — never a fraction, see
          `InstagramReel.tsx`'s identical note: this is an unpublished
          draft with no real playback to report. */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20" aria-hidden>
        <div className="h-full w-0 bg-white/80" />
      </div>
    </div>
  );
}

export const StoryAdPreview: React.FC<StoryAdPreviewProps> = ({
  brandName,
  brandColor = STORY_AD_DEFAULT_COLOR,
  avatarUrl,
  bodyText,
  imageUrl,
  videoUrl,
  aspectRatio,
  overlay,
  isLoading = false,
  emptyHint = 'No creative yet',
  cta,
  embedded = false,
  fullBleed = false,
  chrome = 'story',
  className,
}) => {
  const hasMedia = Boolean(imageUrl) || Boolean(videoUrl) || isLoading;
  const mediaRatio = resolveAspectRatio(aspectRatio, STORY_AD_MEDIA_RATIO);
  const surroundBottom = darken(brandColor, 0.25);
  const avatarIsDark = isDarkColor(brandColor);
  // Full-bleed sizes the CANVAS itself from the requested ratio (a real
  // Story/Reels frame is genuinely ~9:16, not the Figma sample's 338×718
  // proxy for it — see the `fullBleed` doc comment). Non-full-bleed keeps
  // the original ported canvas ratio unconditionally, matching the Figma
  // frame regardless of what `aspectRatio` a caller passes in — that prop
  // has only ever driven the MEDIA band's ratio in this layout.
  const canvasRatio = fullBleed ? resolveAspectRatio(aspectRatio, STORY_AD_RATIO) : STORY_AD_RATIO;

  return (
    <div
      className={cn(embedded ? 'w-full' : 'mx-auto w-full', className)}
      style={embedded ? undefined : { maxWidth: `${STORY_AD_MAX_WIDTH}px` }}
    >
      <article
        className="relative w-full overflow-hidden rounded-xl"
        data-platform="story-ad"
        style={{
          aspectRatio: canvasRatio,
          background: fullBleed
            ? undefined
            : `linear-gradient(180deg, ${brandColor}, ${surroundBottom})`,
        }}
      >
        {fullBleed ? (
          <>
            <MediaSlot
              fill
              overlay={overlay}
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              isLoading={isLoading}
              emptyHint={emptyHint}
              alt={`${brandName} sponsored ${chrome === 'reels' ? 'reels' : 'story'} creative`}
              bg="zinc"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                // Fixed 2026-09-03: the prior gradient held a FULLY
                // TRANSPARENT flat band (25%-60% story / 30%-55% reels) with
                // only 0.55-0.65 alpha at the very bottom edge — nowhere
                // near enough over a bright photo (measured ~1.6:1 against
                // a real sunlit-field creative, AA floor is 4.5:1). No stop
                // below drops to 0 any more, and the bottom band (where the
                // clamped body/CTA text actually sits, per
                // `StoryFullBleedOverlay`/`ReelsFullBleedOverlay`) holds
                // 0.85 flat — sufficient even over a near-white background
                // (composited luminance ≈0.15, contrast ≈5.25:1).
                background:
                  chrome === 'reels'
                    ? 'linear-gradient(180deg, rgb(0 0 0 / 0.3) 0%, rgb(0 0 0 / 0.12) 25%, rgb(0 0 0 / 0.12) 45%, rgb(0 0 0 / 0.85) 65%, rgb(0 0 0 / 0.85) 100%)'
                    : 'linear-gradient(180deg, rgb(0 0 0 / 0.4) 0%, rgb(0 0 0 / 0.12) 22%, rgb(0 0 0 / 0.12) 42%, rgb(0 0 0 / 0.85) 68%, rgb(0 0 0 / 0.85) 100%)',
              }}
              aria-hidden
            />
            {chrome === 'reels' ? (
              <ReelsFullBleedOverlay
                brandName={brandName}
                brandColor={brandColor}
                avatarUrl={avatarUrl}
                bodyText={bodyText}
                cta={cta}
              />
            ) : (
              <StoryFullBleedOverlay
                brandName={brandName}
                brandColor={brandColor}
                avatarUrl={avatarUrl}
                bodyText={bodyText}
                cta={cta}
              />
            )}
          </>
        ) : (
          <div className="flex h-full flex-col px-4 pt-2">
            {/* Progress at zero — see file header. */}
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/35" aria-hidden>
              <div className="h-full w-0 rounded-full bg-white" />
            </div>

            <header className="mt-4 flex items-center gap-3">
              <PreviewAvatar
                brandName={brandName}
                brandColor={brandColor}
                avatarUrl={avatarUrl}
                className="size-9 rounded-full border border-white/40"
                textClassName={cn('text-caption font-semibold', avatarIsDark ? 'text-white' : '')}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{brandName}</p>
                <p className="text-detail text-white/75">Sponsored</p>
              </div>
              <PlatformMoreHorizIcon size={18} className="shrink-0 text-white/80" />
              <PlatformCloseIcon size={16} className="shrink-0 text-white/90" />
            </header>

            <div className="mt-4 overflow-hidden rounded-lg">
              <MediaSlot
                ratio={mediaRatio}
                overlay={overlay}
                imageUrl={imageUrl}
                videoUrl={videoUrl}
                isLoading={isLoading}
                emptyHint={emptyHint}
                alt={`${brandName} sponsored story creative`}
                bg="zinc"
              />
            </div>

            {!hasMedia && <p className="mt-2 text-detail text-white/70">{emptyHint}</p>}

            {bodyText ? (
              <p className="mt-4 whitespace-pre-line text-sm font-semibold leading-relaxed text-white">
                {bodyText}
              </p>
            ) : null}

            <div className="mt-auto flex flex-col items-center gap-2 pb-6 pt-4">
              {cta ? (
                <>
                  <SwipeUpChevron className="size-4 text-white" />
                  <div className="flex h-8 min-w-[84px] items-center justify-center rounded-full bg-white px-4 text-detail font-semibold text-black">
                    {cta}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};
